import type { SheetColumn } from '@/types/custom-playbooks';

/**
 * A pasted Google Sheets link (Sheet trigger and Export step), so a sheet can
 * be set without a Google connection. Mirrors the server's
 * `play_export_step.spreadsheet_link`; the server checks it again.
 */
export interface SheetLink {
  spreadsheetId: string;
  gid: number | null;
}

const SHEET_PATH = /^\/spreadsheets\/d\/([A-Za-z0-9_-]{10,200})(?:\/.*)?$/;

export function sheetLink(text: string | null | undefined): SheetLink | null {
  if (typeof text !== 'string' || text.length > 2000) return null;
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    return null;
  }
  if (
    url.protocol !== 'https:' ||
    url.hostname !== 'docs.google.com' ||
    url.username ||
    url.password ||
    (url.port !== '' && url.port !== '443')
  )
    return null;
  const match = SHEET_PATH.exec(url.pathname);
  if (!match) return null;
  const gids = new Set([
    ...url.searchParams.getAll('gid'),
    ...new URLSearchParams(url.hash.replace(/^#/, '')).getAll('gid'),
  ]);
  const [gid] = [...gids];
  if (gids.size > 1 || (gid !== undefined && !/^[0-9]+$/.test(gid)))
    return null;
  return {
    spreadsheetId: match[1],
    gid: gid === undefined ? null : Number(gid),
  };
}

/** The link for a saved sheet and tab. */
export function sheetUrl(spreadsheetId: string, gid?: number | null): string {
  return `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit${
    gid == null ? '' : `#gid=${gid}`
  }`;
}

/** A trigger column key for a typed header: lowercase words joined by `_`,
 *  starting with a letter, unique among `taken`. */
export function columnKey(header: string, taken: ReadonlySet<string>): string {
  let base = header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!/^[a-z]/.test(base)) base = base ? `col_${base}` : 'column';
  base = base.slice(0, 36);
  let key = base;
  for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
  return key;
}

/** Columns typed by hand, keyed from their headers in order. */
export function keyedColumns(
  columns: Omit<SheetColumn, 'key'>[]
): SheetColumn[] {
  const taken = new Set<string>();
  return columns.map((column) => {
    const key = columnKey(column.header, taken);
    taken.add(key);
    return { ...column, key };
  });
}
